import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fade,
  Card,
  CardContent,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Message as ChatIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  MoreVertRounded as MoreIcon,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// ============ TYPES & INTERFACES ============
interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  type?: 'text' | 'error' | 'success';
  metadata?: {
    orderId?: string;
    shipmentId?: string;
    estimatedDelivery?: string;
  };
}

interface PreDefinedOption {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  action: string;
  category: 'shipment' | 'delivery' | 'support' | 'tracking';
}

interface ApiPayload {
  message: string;
  conversationId: string;
  userId?: string;
  context?: Record<string, any>;
  timestamp: Date;
}

interface BotResponse {
  success: boolean;
  message: string;
  data?: Record<string, any>;
  error?: string;
  conversationId: string;
}

// ============ ANIMATIONS ============
const slideInUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 0 40px rgba(59, 130, 246, 0.8);
  }
`;

const messageSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

// ============ STYLED COMPONENTS ============
const GlassContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  bottom: 20,
  right: 20,
  width: 420,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1300,
  borderRadius: 24,
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 8px 32px rgba(31, 38, 135, 0.25)',
  animation: `${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 16px)',
    maxHeight: '85vh',
    bottom: 10,
    right: 8,
  },
}));

const FloatingBotButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  bottom: 124,
  right: 24,
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  boxShadow: `0 8px 24px ${theme.palette.primary.main}66`,
  animation: `${pulseGlow} 2s infinite`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 12px 32px ${theme.palette.primary.main}99`,
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  [theme.breakpoints.down('sm')]: {
    bottom: 20,
    right: 16,
    width: 56,
    height: 56,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  borderRadius: '24px 24px 0 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
}));

const MessagesContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(59, 130, 246, 0.4)',
    borderRadius: '10px',
    '&:hover': {
      background: 'rgba(59, 130, 246, 0.6)',
    },
  },
}));

const UserMessage = styled(Box)(({ theme }) => ({
  alignSelf: 'flex-end',
  maxWidth: '85%',
  padding: '12px 16px',
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  wordWrap: 'break-word',
  boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
  animation: `${messageSlideIn} 0.3s ease-out`,
  fontWeight: 500,
}));

const BotMessage = styled(Card)(({ theme }) => ({
  alignSelf: 'flex-start',
  maxWidth: '85%',
  borderRadius: '16px',
  background: `rgba(${parseInt(theme.palette.primary.main.slice(1, 3), 16)}, ${parseInt(
    theme.palette.primary.main.slice(3, 5),
    16,
  )}, ${parseInt(theme.palette.primary.main.slice(5, 7), 16)}, 0.08)`,
  border: `1px solid ${theme.palette.primary.main}1A`,
  animation: `${messageSlideIn} 0.3s ease-out`,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  '& .MuiCardContent-root': {
    padding: '12px 16px',
    color: theme.palette.text.primary,
    '&:last-child': {
      paddingBottom: '12px',
    },
  },
}));

const TypingIndicator = styled(Box)(() => ({
  display: 'flex',
  gap: '4px',
  alignItems: 'flex-end',
  padding: '12px 16px',
  '& > span': {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#3b82f6',
  },
  '& span:nth-of-type(1)': {
    animation: `${typingAnimation} 1.4s infinite`,
  },
  '& span:nth-of-type(2)': {
    animation: `${typingAnimation} 1.4s infinite 0.2s`,
  },
  '& span:nth-of-type(3)': {
    animation: `${typingAnimation} 1.4s infinite 0.4s`,
  },
}));

const InputSection = styled(Box)(() => ({
  padding: '12px 16px',
  borderTop: '1px solid rgba(0, 0, 0, 0.08)',
  display: 'flex',
  gap: 8,
  background: 'rgba(255, 255, 255, 0.5)',
  borderRadius: '0 0 24px 24px',
}));

const OptionCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  borderRadius: 12,
  background: `linear-gradient(135deg, ${theme.palette.secondary.main}0D 0%, ${theme.palette.secondary.main}05 100%)`,
  border: `1.5px solid ${theme.palette.secondary.main}33`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.secondary.main}1F 0%, ${theme.palette.secondary.main}0F 100%)`,
    border: `1.5px solid ${theme.palette.secondary.main}66`,
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${theme.palette.secondary.main}33`,
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

// ============ PRE-DEFINED OPTIONS ============
const PREDEFINED_OPTIONS: PreDefinedOption[] = [
  {
    id: 'track-shipment',
    label: 'Track Shipment',
    icon: '📦',
    description: 'Track your shipment in real-time',
    action: 'track_shipment',
    category: 'tracking',
  },
  {
    id: 'estimate-delivery',
    label: 'Delivery Estimate',
    icon: '⏱️',
    description: 'Get estimated delivery time',
    action: 'estimate_delivery',
    category: 'delivery',
  },
  {
    id: 'cancel-order',
    label: 'Cancel Order',
    icon: '❌',
    description: 'Cancel your shipment',
    action: 'cancel_order',
    category: 'shipment',
  },
  {
    id: 'support-help',
    label: 'Get Support',
    icon: '🆘',
    description: 'Chat with support team',
    action: 'connect_support',
    category: 'support',
  },
  {
    id: 'rates-pricing',
    label: 'Check Rates',
    icon: '💰',
    description: 'View shipping rates',
    action: 'get_rates',
    category: 'shipment',
  },
  {
    id: 'return-process',
    label: 'Return Process',
    icon: '↩️',
    description: 'Learn about returns',
    action: 'return_process',
    category: 'support',
  },
];

// ============ MAIN COMPONENT ============
export const LogisticsAIBot: React.FC = () => {
  // State Management
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState('');
  const [showOptions, setShowOptions] = useState(true);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize conversation on component mount
  useEffect(() => {
    const newConvId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setConversationId(newConvId);
  }, []);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle sending message to backend
  const sendMessageToBackend = useCallback(
    async (message: string) => {
      const payload: ApiPayload = {
        message,
        conversationId,
        timestamp: new Date(),
        context: {
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      try {
        // Replace with your actual backend URL
        const response = await fetch('/api/logistics-bot/message', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.statusText}`);
        }

        const data: BotResponse = await response.json();

        if (data.success) {
          return {
            text: data.message,
            type: 'text' as const,
            metadata: data.data,
          };
        } else {
          return {
            text: data.error || 'Sorry, something went wrong. Please try again.',
            type: 'error' as const,
          };
        }
      } catch (error) {
        console.error('Error sending message:', error);
        return {
          text: 'Unable to connect to the server. Please check your connection and try again.',
          type: 'error' as const,
        };
      }
    },
    [conversationId],
  );

  // Send message handler
  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim()) return;

    // Add user message
    const userMsg: Message = {
      id: `msg_${Date.now()}`,
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setShowOptions(false);
    setIsLoading(true);

    try {
      const response = await sendMessageToBackend(inputValue);

      const botMsg: Message = {
        id: `msg_${Date.now()}_bot`,
        text: response.text,
        sender: 'bot',
        timestamp: new Date(),
        type: response.type,
        metadata: response.metadata,
      };

      setMessages(prev => [...prev, botMsg]);
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, sendMessageToBackend]);

  // Handle predefined option click
  const handleOptionClick = useCallback(
    (option: PreDefinedOption) => {
      setMessages([]);
      setShowOptions(false);

      const msg: Message = {
        id: `msg_${Date.now()}`,
        text: option.label,
        sender: 'user',
        timestamp: new Date(),
      };

      setMessages([msg]);
      setIsLoading(true);

      // Simulate sending action to backend
      setTimeout(async () => {
        const response = await sendMessageToBackend(option.action);

        const botMsg: Message = {
          id: `msg_${Date.now()}_bot`,
          text: response.text,
          sender: 'bot',
          timestamp: new Date(),
          type: response.type,
          metadata: response.metadata,
        };

        setMessages(prev => [...prev, botMsg]);
        setIsLoading(false);
      }, 500);
    },
    [sendMessageToBackend],
  );

  // Handle keyboard event
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  // Clear conversation
  const handleClearConversation = useCallback(() => {
    setMessages([]);
    setShowOptions(true);
    setShowClearDialog(false);
  }, []);

  // Download conversation
  const handleDownloadConversation = useCallback(() => {
    const conversationText = messages
      .map(msg => `[${msg.timestamp.toLocaleTimeString()}] ${msg.sender.toUpperCase()}: ${msg.text}`)
      .join('\n\n');

    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(conversationText)}`);
    element.setAttribute('download', `logistics-chat-${conversationId}.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [messages, conversationId]);

  // If closed, show only floating button
  if (!isOpen) {
    return (
      <Tooltip title="Logistics AI Bot" arrow placement="left">
        <FloatingBotButton
          onClick={() => {
            setIsOpen(true);
            setIsMinimized(false);
          }}
        >
          <ChatIcon />
        </FloatingBotButton>
      </Tooltip>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <Fade in={true}>
        <GlassContainer>
          <Header>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChatIcon />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Logistics AI
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Expand" arrow>
                <IconButton size="small" onClick={() => setIsMinimized(false)} sx={{ color: 'white' }}>
                  <MinimizeIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Header>
        </GlassContainer>
      </Fade>
    );
  }

  // Full chat interface
  return (
    <Fade in={true}>
      <GlassContainer elevation={0}>
        {/* Header */}
        <Header>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
              }}
            >
              <ChatIcon />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Logistics AI Assistant
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {isLoading ? 'Typing...' : 'Always ready to help'}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Tooltip title="More options" arrow>
              <IconButton size="small" onClick={() => setShowClearDialog(true)} sx={{ color: 'white' }}>
                <MoreIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Minimize" arrow>
              <IconButton size="small" onClick={() => setIsMinimized(true)} sx={{ color: 'white' }}>
                <MinimizeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Close" arrow>
              <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Header>

        {/* Messages or Initial Options */}
        <MessagesContainer>
          {messages.length === 0 && showOptions ? (
            // Welcome state with predefined options
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 2 }}>
              <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 1 }}>
                How can I help you today?
              </Typography>
              {PREDEFINED_OPTIONS.map(option => (
                <OptionCard key={option.id} onClick={() => handleOptionClick(option)}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ fontSize: '24px', flexShrink: 0 }}>{option.icon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                          {option.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </OptionCard>
              ))}
            </Box>
          ) : (
            // Messages display
            <>
              {messages.map(message => (
                <Box key={message.id}>
                  {message.sender === 'user' ? (
                    <UserMessage>{message.text}</UserMessage>
                  ) : (
                    <BotMessage>
                      <CardContent sx={{ color: 'text.primary' }}>
                        {message.text}
                        {message.metadata && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ mt: 1, fontSize: '0.85rem', color: 'text.secondary' }}>
                              {message.metadata.orderId && (
                                <Chip
                                  label={`Order: ${message.metadata.orderId}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 1, mb: 0.5 }}
                                />
                              )}
                              {message.metadata.shipmentId && (
                                <Chip
                                  label={`Shipment: ${message.metadata.shipmentId}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 1, mb: 0.5 }}
                                />
                              )}
                              {message.metadata.estimatedDelivery && (
                                <Chip
                                  label={`Delivery: ${message.metadata.estimatedDelivery}`}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </>
                        )}
                      </CardContent>
                    </BotMessage>
                  )}
                </Box>
              ))}
              {isLoading && (
                <Box sx={{ alignSelf: 'flex-start' }}>
                  <TypingIndicator>
                    <span />
                    <span />
                    <span />
                  </TypingIndicator>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesContainer>

        {/* Input Section */}
        <InputSection>
          <TextField
            fullWidth
            placeholder="Type your message..."
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            multiline
            maxRows={2}
            size="small"
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(8px)',
                '& fieldset': {
                  borderColor: `${theme.palette.primary.main}33`,
                },
                '&:hover fieldset': {
                  borderColor: `${theme.palette.primary.main}66`,
                },
                '&.Mui-focused fieldset': {
                  borderColor: `${theme.palette.primary.main}99`,
                },
              },
            }}
          />
          <Tooltip title="Send" arrow>
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              sx={{
                color: '#3b82f6',
                '&:hover': { background: 'rgba(59, 130, 246, 0.1)' },
              }}
            >
              {isLoading ? <CircularProgress /> : <SendIcon />}
            </IconButton>
          </Tooltip>
        </InputSection>

        {/* Menu Dialog */}
        <Dialog open={showClearDialog} onClose={() => setShowClearDialog(false)}>
          <DialogTitle>Chat Options</DialogTitle>
          <DialogContent sx={{ minWidth: 320 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Manage your conversation
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button startIcon={<RefreshIcon />} onClick={handleClearConversation} variant="outlined">
              Clear Chat
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={handleDownloadConversation} variant="outlined">
              Download
            </Button>
            <Button onClick={() => setShowClearDialog(false)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </GlassContainer>
    </Fade>
  );
};

export default LogisticsAIBot;
